import { useEffect, useState } from 'react'
import { LayoutDashboard, Users, UserCog, Settings, Send, LogOut } from 'lucide-react'
import { useLocation, Link, useNavigate } from 'react-router'
import { cn } from '@/lib/utils'
import eyeLogo from '@/assets/Landing/eyelogo.svg'
import { useAuth } from '@/context/AuthContext'
import { organizationApi } from '@/utils/api'
import { resolveBackendUrl } from '@/utils/media'
import { OrgPermission } from '@/utils/permissions'

interface OrganizationSidebarContentProps {
  className?: string
}

export function OrganizationSidebarContent({ className }: OrganizationSidebarContentProps) {
  const location = useLocation()
  const navigate = useNavigate()
  const { logout, user, hasPermission } = useAuth()
  const [orgName, setOrgName] = useState<string>('')
  const [orgLogo, setOrgLogo] = useState<string>('')

  useEffect(() => {
    const orgId = (user as { organizationId?: string })?.organizationId
    if (!orgId) return
    organizationApi.getOrganization(orgId).then(res => {
      const data = (res as { data?: { name?: string | null; logo?: string | null } }).data || res
      const org = data as { name?: string | null; logo?: string | null }
      if (org.name) setOrgName(org.name)
      if (org.logo) setOrgLogo(resolveBackendUrl(org.logo))
    }).catch(() => {})
  }, [user])

  const allMenuItems = [
    {
      name: 'Analytics',
      path: '/organization/analytics',
      icon: LayoutDashboard,
      permission: OrgPermission.VIEW_STATISTICS,
    },
    {
      name: 'Couple Management',
      path: '/organization/couples',
      icon: Users,
      permission: OrgPermission.VIEW_COUPLES,
    },
    {
      name: 'Internal User Management',
      path: '/organization/users',
      icon: UserCog,
      permission: OrgPermission.MANAGE_MEMBERS,
    },
    {
      name: 'Settings',
      path: '/organization/settings',
      icon: Settings,
      permission: OrgPermission.MANAGE_ORGANIZATION,
    },
  ]

  const menuItems = allMenuItems.filter((item) => hasPermission(item.permission))

  return (
    <div className={cn('flex flex-col h-full bg-[#F8F9FA]', className)}>
      {/* Logo */}
      <div className='px-6 py-6'>
        <div className='flex items-center gap-2'>
          <img src={eyeLogo} alt='IRIS Logo' className='h-8 w-8' />
          <span className='text-[24px] font-bold text-[#151515]'>IRIS</span>
        </div>
        <p className='text-[10px] text-[#4e4d4d] mt-1'>
          Intimate Relationship Indicators Screening
        </p>
      </div>

      {/* Organization Card */}
      {orgName && (
        <div className='mx-4 mb-6'>
          <div className='flex items-center gap-3 rounded-[12px] bg-white px-4 py-4'>
            {orgLogo ? (
              <img
                src={orgLogo}
                alt={orgName}
                className='h-10 w-10 rounded-full object-cover flex-shrink-0'
              />
            ) : (
              <div className='h-10 w-10 rounded-full bg-[#F3F4F6] flex items-center justify-center flex-shrink-0'>
                <span className='text-[14px] font-bold text-[#4e4d4d]'>
                  {orgName.charAt(0)}
                </span>
              </div>
            )}
            <p className='text-[14px] font-semibold text-[#151515] leading-tight'>
              {orgName} Dashboard
            </p>
          </div>
        </div>
      )}

      {/* Couples Invitation - Standalone Section */}
      {hasPermission(OrgPermission.INVITE_COUPLES) && (
        <div className='mx-4 mb-6'>
          <Link
            to='/organization/invitation'
            className='block bg-[#151515] hover:bg-[#2a2a2a] rounded-[12px] p-4 transition-colors group'
          >
            <div className='flex items-center gap-3 mb-2'>
              <Send className='h-5 w-5 text-[#FA6017]' />
              <h3 className='text-[16px] font-bold text-white'>Couples Invitation</h3>
            </div>
            <div className='flex items-center justify-between'>
              <p className='text-[12px] text-[#A0A0A0]'>Register for a Couples' Assessment</p>
              <svg
                className='h-5 w-5 text-[#FA6017] group-hover:translate-x-1 transition-transform'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M9 5l7 7-7 7'
                />
              </svg>
            </div>
          </Link>
        </div>
      )}

      {/* Navigation Menu */}
      <nav className='flex-1 px-2 overflow-y-auto'>
        {menuItems.map((item) => {
          const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/')
          const Icon = item.icon

          return (
            <Link
              key={item.path}
              to={item.path}
              className={cn(
                'relative flex items-center gap-3 px-4 py-3 text-[14px] font-medium transition-colors',
                isActive
                  ? 'text-[#FA6017]'
                  : 'text-[#4e4d4d] hover:text-[#151515]'
              )}
            >
              {/* Active indicator - orange bar on left edge */}
              {isActive && (
                <div className='absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-[24px] bg-[#FA6017] rounded-r-full' />
              )}

              <Icon
                className={cn(
                  'h-5 w-5 shrink-0',
                  isActive ? 'text-[#FA6017]' : 'text-[#4e4d4d]'
                )}
              />
              <span>{item.name}</span>
            </Link>
          )
        })}
      </nav>

      {/* Logout Button */}
      <div className='px-4 py-4 border-t border-[#E5E7EB]'>
        <button
          onClick={() => {
            logout()
            navigate('/login', { replace: true })
          }}
          className='w-full flex items-center gap-3 px-4 py-3 text-[14px] font-medium text-[#EF4444] hover:bg-red-50 rounded-lg transition-colors'
        >
          <LogOut className='h-5 w-5' />
          <span>Logout</span>
        </button>
      </div>
    </div>
  )
}

export default OrganizationSidebarContent
